/* eslint-disable consistent-return */

import {
  forgotPasswordMsgError,
  forgotPasswordMsgSuccess,
  resendVerificationEmailSuccess,
  resetPasswordMsgSuccess,
  signUpMsgError,
  somethingWentWrong,
} from '@gameday/common/constants/messages'
import { isEmpty } from 'lodash'
import { all, put, select, takeLatest } from 'redux-saga/effects'

import history from '../_history'
import { auth as authActions, common, formInfo } from '../constants/actions'
import { auth, common as commonEndpoints, formSettings } from '../constants/endpoints'
import routes from '../constants/routes/routes'
import {
  getFormId,
  getFormType,
  getOrganisationId,
  getSubdomain,
  getUser,
  getUserEmail,
  getUserId,
  getUserName,
} from '../selectors'
import { getSubdomain as getSubdomainPath, toDateOnly } from '../utils/common'
import { encryptRsa } from '../utils/encryption'
import { checkAuth, get, post } from '../utils/request'

export function* getPublicCert() {
  const response = yield get({
    endpointId: auth.getPublicCert,
  })
  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (response && response.data) {
    yield put(authActions.setPublicCert(response.data))
  }
}

export function* signin({ payload }) {
  yield put(common.setShowLoadingIndicator(true))
  const { email, password } = payload

  const totResponse = yield get({
    endpointId: auth.getTot,
  })

  // biome-ignore lint/complexity/useOptionalChain: TODO(biome-2.4): newly detected in 2.4 upgrade
  if (!totResponse || !totResponse.data) {
    // show login error, 'Something went wrong'
    yield put(common.setShowLoadingIndicator(false))
    return
  }
  const loginData = {
    password: password.value,
    tot: totResponse.data,
  }

  const encryptedPass = yield encryptRsa(JSON.stringify(loginData))
  const response = yield get({
    endpointId: auth.signin,
    params: {
      email: email.value,
      password: encryptedPass,
      application: 'synergy',
      encrypted: true,
    },
  })

  // handle login error
  if (!response || response.status > 300) {
    yield put(common.setShowLoadingIndicator(false))
    switch (response.status) {
      case 400:
        return yield put(
          authActions.setLoginError({
            type: 'account',
            message: response.errorMessage || response.error,
          })
        )
      case 401:
        return yield put(
          authActions.setLoginError({
            type: 'password',
            message: response.errorMessage || response.error,
          })
        )
      case 403:
        return yield put(
          authActions.setLoginError({
            type: 'verify',
            message: response.errorMessage || response.error,
          })
        )
      default:
        return yield put(
          authActions.setLoginError({
            type: 'error',
            message: response.errorMessage || response.error || somethingWentWrong,
          })
        )
    }
  }

  yield completeSignIn(email.value)
}

export function* checkAuthSaga({ payload }) {
  const { expired } = payload || {}

  if (expired) {
    yield put(common.setShowLoadingIndicator(false))
    yield put(authActions.setIsCheckingAuth(false))
    return
  }

  yield put(common.setShowLoadingIndicator(true))

  const response = yield checkAuth()

  if (!response || response.status > 300 || !response.success) {
    yield put(common.setShowLoadingIndicator(false))
    yield put(authActions.setIsCheckingAuth(false))
    return
  }

  const email = yield select(getUserEmail)

  yield completeSignIn(email)
}

function* completeSignIn(email) {
  // get user information from token
  const userName = yield select(getUserName)
  const userid = yield select(getUserId)

  // set user information (minimal — preserves existing timing)
  yield put(
    authActions.setUser({
      email,
      userName: `${userName.firstName} ${userName.lastName}`,
      firstName: userName.firstName,
      lastName: userName.lastName,
      userid,
    })
  )

  // fetch form details and account details in parallel
  const [formDetailResponse, accountResponse] = yield all([
    get({
      endpointId: formSettings.getFormDetails,
      params: {
        form: yield select(getFormId),
        organisation: yield select(getOrganisationId),
        user: userid,
      },
    }),
    get({
      endpointId: auth.getUserAccount,
    }),
  ])

  // set form data
  if (formDetailResponse.data) {
    yield put(formInfo.setFormInfo(formDetailResponse.data))
  }

  // enrich user with full account details (graceful degradation if call fails)
  if (accountResponse?.data) {
    yield put(
      authActions.setUser({
        email,
        userName: `${userName.firstName} ${userName.lastName}`,
        firstName: userName.firstName,
        lastName: userName.lastName,
        userid,
        birthDate: accountResponse.data.birthDate,
        genderIdentity: accountResponse.data.genderIdentity,
        phone: accountResponse.data.phone,
        country: accountResponse.data.country,
      })
    )
  }

  yield put(common.setShowLoadingIndicator(false))
  yield put(authActions.setIsCheckingAuth(false))

  history.push({
    pathname: routes.form.path,
    search: history.location.search,
  })
}

export function* signup({ payload }) {
  const formType = yield select(getFormType)
  const subdomain = yield select(getSubdomain)
  const sd = getSubdomainPath()

  yield put(common.setShowLoadingIndicator(true))

  const { email, firstname, lastname, password, birthDate, country, genderIdentity, mobile } = payload

  const data =
    formType === 'event'
      ? {
          subdomain: subdomain || sd,
        }
      : {
          form: yield select(getFormId),
          account: yield select(getOrganisationId),
        }

  const encryptedPass = yield encryptRsa(password.value)

  const optionalFields = {
    ...(birthDate?.value && { birthDate: toDateOnly(birthDate.value) }),
    ...(country?.value?.value && { country: country.value.value }),
    ...(genderIdentity?.value && { genderIdentity: genderIdentity.value }),
    ...(mobile?.value && { mobile: mobile.value }),
  }

  const response = yield post({
    endpointId: auth.signup,
    data: {
      email: email.value.toLowerCase(),
      firstName: firstname.value,
      lastName: lastname.value,
      password: encryptedPass,
      application: 'synergy',
      ...optionalFields,
      ...data,
    },
  })

  // handle signup error
  if (!response || response.status > 300) {
    yield put(common.setShowLoadingIndicator(false))
    switch (response.status) {
      case 403:
        return yield put(
          authActions.setSignupMsg({
            type: 'verify',
            message: signUpMsgError,
          })
        )
      default:
        return yield put(
          authActions.setSignupMsg({
            type: 'error',
            message: response.errorMessage || response.error || somethingWentWrong,
          })
        )
    }
  } else {
    yield put(
      authActions.setUser({
        email: email.value,
      })
    )
  }

  // redirect to signup success page
  yield put(common.setShowLoadingIndicator(false))

  history.push({
    pathname: routes.signupSuccess.path,
    search: history.location.search,
  })
}

export function* signupGuest({ payload }) {
  yield put(common.setShowLoadingIndicator(true))

  const { email, firstname, lastname, birthDate, country, genderIdentity, mobile } = payload

  const optionalFields = {
    ...(birthDate?.value && { birthDate: toDateOnly(birthDate.value) }),
    ...(country?.value?.value && { country: country.value.value }),
    ...(genderIdentity?.value && { genderIdentity: genderIdentity.value }),
    ...(mobile?.value && { mobile: mobile.value }),
  }

  const response = yield post({
    endpointId: auth.signupGuest,
    data: {
      email: email.value.toLowerCase(),
      firstName: firstname.value,
      lastName: lastname.value,
      application: 'synergy',
      subdomain: 'N/A',
      ...optionalFields,
    },
  })

  // handle signup error
  if (!response || response.status > 300) {
    yield put(common.setShowLoadingIndicator(false))
    switch (response?.status) {
      case 403:
        return yield put(
          authActions.setSignupGuestMsg({
            type: 'verify',
            message: signUpMsgError,
          })
        )
      default: {
        return yield put(
          authActions.setSignupGuestMsg({
            type: 'error',
            message: response.error || somethingWentWrong,
          })
        )
      }
    }
  }

  // get user information from token
  const userid = yield select(getUserId)

  // populate user state with the values the guest just entered so Basic Info
  // pre-fills on the Participants page (mirrors GAME-248 for the guest path)
  yield put(
    authActions.setUser({
      email: email.value,
      userName: `${firstname.value} ${lastname.value}`,
      firstName: firstname.value,
      lastName: lastname.value,
      userid,
      ...(birthDate?.value && { birthDate: toDateOnly(birthDate.value) }),
      ...(genderIdentity?.value && { genderIdentity: genderIdentity.value }),
      ...(mobile?.value && { phone: mobile.value }),
      ...(country?.value?.value && { country: country.value.value }),
    })
  )

  const formDetailResponse = yield get({
    endpointId: formSettings.getFormDetails,
    params: {
      form: yield select(getFormId),
      organisation: yield select(getOrganisationId),
    },
  })

  if (formDetailResponse.data) {
    yield put(formInfo.setFormInfo(formDetailResponse.data))
  }

  yield put(common.setShowLoadingIndicator(false))

  history.push({
    pathname: routes.form.path,
    search: history.location.search,
  })
}

export function* verifyEmail({ payload }) {
  yield put(common.setShowLoadingIndicator(true))

  const { email, token } = payload

  const response = yield get({
    endpointId: auth.verifyEmail,
    params: {
      email,
      token,
    },
  })

  if (response.success) {
    yield put(authActions.emailIsVerified())
  } else {
    yield put(authActions.emailVerificationError({ type: 'error', message: response.errorMessage }))
  }

  yield put(common.setShowLoadingIndicator(false))
}

export function* forgotPassword({ payload }) {
  const subdomain = yield select(getSubdomain)
  const sd = getSubdomainPath()
  const formType = yield select(getFormType)

  yield put(common.setShowLoadingIndicator(true))

  const { email } = payload

  const data =
    formType === 'event'
      ? {
          subdomain: subdomain || sd,
        }
      : {
          form: yield select(getFormId),
          account: yield select(getOrganisationId),
        }

  const response = yield post({
    endpointId: auth.resetPassword,
    data: {
      email: email.value,
      application: 'synergy',
      ...data,
    },
  })

  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (response && response.success) {
    yield put(authActions.setForgotPasswordMsg({ type: 'success', message: forgotPasswordMsgSuccess }))
  } else {
    yield put(authActions.setForgotPasswordMsg({ type: 'error', message: forgotPasswordMsgError }))
  }

  yield put(common.setShowLoadingIndicator(false))
}

export function* resetPassword({ payload }) {
  yield put(common.setShowLoadingIndicator(true))

  const { email, password, token, application } = payload

  const encryptedPass = yield encryptRsa(password)

  const response = yield post({
    endpointId: auth.changePassword,
    data: {
      email,
      password: encryptedPass,
      application,
      verificationToken: token,
    },
  })

  if (response.success) {
    yield put(authActions.setResetPasswordMsg({ type: null, message: resetPasswordMsgSuccess }))
  } else {
    yield put(authActions.setResetPasswordMsg({ type: 'error', message: response.errorMessage }))
  }

  yield put(common.setShowLoadingIndicator(false))
}

export function* signout(action) {
  // biome-ignore lint/complexity/useOptionalChain: TODO
  if (action.payload && action.payload.e) {
    action.payload.e.preventDefault()
  }
  if (action.payload !== true) {
    history.push({
      pathname: routes.login.path,
      search: history.location.search,
    })
  }
  const { email } = yield select(getUser)
  if (email) {
    yield post({
      endpointId: auth.signout,
      data: {
        email,
      },
    })
  }

  yield put(authActions.clearUser())
  yield put(authActions.setIsCheckingAuth(false))
}

export function* resendVerificationEmail({ payload }) {
  const formType = yield select(getFormType)
  const subdomain = yield select(getSubdomain)
  const sd = getSubdomainPath()

  yield put(common.setShowLoadingIndicator(true))

  const { email } = payload

  const data =
    formType === 'event'
      ? {
          subdomain: subdomain || sd,
        }
      : {
          form: yield select(getFormId),
          account: yield select(getOrganisationId),
        }

  const response = yield post({
    endpointId: auth.sendVerificationEmail,
    data: {
      email: email.value,
      application: 'synergy',
      ...data,
    },
  })

  if (response.success) {
    yield put(authActions.setLoginMsg({ type: null, message: resendVerificationEmailSuccess }))
  } else {
    yield put(authActions.setLoginError({ type: 'error', message: response.error || somethingWentWrong }))
  }

  yield put(common.setShowLoadingIndicator(false))
}

export function* getSystemAlert() {
  const response = yield get({
    endpointId: commonEndpoints.systemalert,
  })
  if (response.data) {
    yield put(common.setSystemAlert(!isEmpty(response.data) ? response.data[0] : {}))
  }
}

export function* runAuthSagas() {
  yield takeLatest(authActions.GET_PUBLIC_CERT, getPublicCert)
  yield takeLatest(common.GET_SYSTEM_ALERT, getSystemAlert)
  yield takeLatest(authActions.SIGN_IN, signin)
  yield takeLatest(authActions.CHECK_AUTH, checkAuthSaga)
  yield takeLatest(authActions.SIGN_UP, signup)
  yield takeLatest(authActions.SIGN_UP_GUEST, signupGuest)
  yield takeLatest(authActions.SIGN_OUT, signout)
  yield takeLatest(authActions.FORGOT_PASSWORD, forgotPassword)
  yield takeLatest(authActions.RESET_PASSWORD, resetPassword)
  yield takeLatest(authActions.VERIFY_EMAIL, verifyEmail)
  yield takeLatest(authActions.RESEND_VERIFICATION_EMAIL, resendVerificationEmail)
}

export default runAuthSagas
